



















































































































































import Vue, { PropType } from 'vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import { RouteHelpers, FormFieldValidation, ModalHelpers } from '@/mixins';
import {
  BaseResultModel,
  CabinetModel,
  CabinetReferenceModel,
  DocumentTypeModel,
  ArenaSignApi,
  ArenaSignIndexActionModel,
  ArenaSignTemplateAfterSignReferenceMappingModel,
  ArenaSignTemplateAfterSignSettingModel,
  ArenaSignTemplateAfterSignConfigurationModel,
  ArenaSignTemplateAfterSignSettingUpdateModel
} from '@/types';
import MStoreFormSelect from '@/components/shared/MStoreFormSelect.vue';
import { DocuSignConfigurationService } from '@/services/docuSign/DocuSignConfigurationService';
import { ScriveConfigurationService } from '@/services/scrive/ScriveConfigurationService';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';

export default Vue.extend({
  name: 'ArenaSignEditTemplateAfterSignMappings',
  mixins: [RouteHelpers, FormFieldValidation, ModalHelpers],
  props: {
    arenaSignTemplateAfterSignConfiguration: {
      type: Object as PropType<ArenaSignTemplateAfterSignConfigurationModel>,
      required: true
    },
    arenaSignApi: {
      type: Number as PropType<ArenaSignApi>,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      submitIsLoading: false,
      hasError: false,
      errorMessage: '',
      template: {} as ArenaSignTemplateAfterSignSettingModel,
      targetCabinets: [] as CabinetModel[],
      indexActions: [
        { id: 1, description: 'New Document' },
        { id: 2, description: 'Version a matching document' }
      ] as ArenaSignIndexActionModel[],
      referenceMappings: {},
      targetCabinetId: '',
      targetDocumentTypeId: 0
    };
  },
  computed: {
    targetCabinet(this: any) {
      return this.targetCabinets?.find((x: CabinetModel) => x.id === this.targetCabinetId);
    },
    targetDocumentType(this: any) {
      return this.targetCabinet?.documentTypes?.find((x: DocumentTypeModel) => x.id === this.targetDocumentTypeId);
    },
    targetReferences(this: any) {
      const targetReferences: CabinetReferenceModel[] = [];
      Object.assign(targetReferences, this.targetDocumentType?.references);
      targetReferences.unshift({
        id: -1,
        referenceDescription: 'No Mapping'
      } as CabinetReferenceModel);
      return targetReferences;
    },
    targetDocumentTypes(): DocumentTypeModel[] {
      return this.targetCabinet == null ? ([] as DocumentTypeModel[]) : this.targetCabinet.documentTypes;
    },
    apiIsScrive(): boolean {
      return this.arenaSignApi === ArenaSignApi.Scrive;
    }
  },
  methods: {
    setupData(this: any) {
      // tslint:disable-next-line:max-line-length
      this.targetCabinets = this.arenaSignTemplateAfterSignConfiguration.targetCabinets;
      this.template = this.arenaSignTemplateAfterSignConfiguration.templateAfterSignSetting;
      this.targetCabinetId = this.arenaSignTemplateAfterSignConfiguration.templateAfterSignSetting.targetCabinet?.id;
      // tslint:disable-next-line:max-line-length
      this.targetDocumentTypeId = this.arenaSignTemplateAfterSignConfiguration.templateAfterSignSetting.targetDocumentType?.id;

      this.template.sourceDocumentType.references.forEach(
        (x: CabinetReferenceModel) => (this.referenceMappings[x.id] = -1)
      );

      this.template.referenceMappings.forEach((x: ArenaSignTemplateAfterSignReferenceMappingModel) => {
        const sourceReference = this.template.sourceDocumentType.references.find(
          (r: CabinetReferenceModel) =>
            this.getReferenceType(r.type) === x.signedReferenceType && x.signedReferenceNumber === r.referenceNumber
        );
        const targetReference = this.template.targetDocumentType.references.find(
          (r: CabinetReferenceModel) =>
            this.getReferenceType(r.type) === x.targetReferenceType && x.targetReferenceNumber === r.referenceNumber
        );
        this.referenceMappings[sourceReference.id] = targetReference.id;
      });
    },
    getReferenceType(this: any, referenceType: number) {
      const referenceTypeMap = ['C', 'D', 'P'];
      return referenceTypeMap[referenceType - 1];
    },
    async onSubmit(this: any) {
      if (!this.validateForm()) {
        return;
      }
      if (this.hasUnmappedReferences()) {
        const unmappedReferences = this.template.sourceDocumentType.references.filter(
          (x: CabinetReferenceModel) => this.referenceMappings[x.id] === -1
        );
        let unmappedReferencesList = '';
        unmappedReferences.forEach((reference: CabinetReferenceModel) => {
          unmappedReferencesList += reference.referenceDescription + ', ';
        });
        const confirmed = await this.showConfirmationModalAsync(
          this.$bvModal,
          `we have noticed these: ${unmappedReferencesList}references are not mapped`
        );

        if (!confirmed) {
          return;
        }
      }
      try {
        this.submitIsLoading = true;
        const result: BaseResultModel = await this.updateTemplateAfterSignSettingAsync();

        if (result.hasNotification && !result.showToast) {
          this.addFormValidationError(result.notification);
          this.submitIsLoading = false;
          return;
        }

        SnotifyConfiguration.showSuccess('Template after sign mappings updated successfully.');
        this.setApplicationDirty(false);
        this.$emit('on-submit-complete');
      } catch (error) {
        this.$log('Error updating template after sign configuration.', error);
      } finally {
        this.submitIsLoading = false;
      }
    },
    onCabinetChanged(this: any) {
      if (
        !this.targetCabinets
          .find((x: CabinetModel) => x.id === this.targetCabinetId)
          .documentTypes.find((x: DocumentTypeModel) => x.id === this.targetDocumentTypeId)
      ) {
        this.targetDocumentTypeId = null;
      }
    },
    onCancel(this: any) {
      this.$emit('on-cancel');
    },
    validateForm(this: any): boolean {
      this.$validator.errors.remove('documentTypeMappingValidationErrors');
      this.$validator.errors.remove('referenceMappingValidationErrors');
      this.resetFormValidation();
      if (!this.targetCabinet?.id || !this.targetDocumentType?.id) {
        this.$validator.errors.add({
          field: 'documentTypeMappingValidationErrors',
          msg: 'You must select a value for all dropdowns'
        });
        return false;
      }

      if (
        !this.targetCabinets
          .find((x: CabinetModel) => x.id === this.targetCabinetId)
          ?.documentTypes.find((x: DocumentTypeModel) => x.id === this.targetDocumentTypeId)
      ) {
        this.$validator.errors.add({
          field: 'documentTypeMappingValidationErrors',
          msg: 'Target Document Type does not exist in Target Cabinet'
        });
        return false;
      }

      if (this.template.indexAction === 2 && Object.values(this.referenceMappings).every((x) => x === -1)) {
        this.$validator.errors.add({
          field: 'referenceMappingValidationErrors',
          msg: 'You must have at least one reference mapping when indexing as a new version'
        });
        return false;
      }

      return true;
    },
    hasUnmappedReferences(): boolean {
      return Object.values(this.referenceMappings).includes(-1);
    },
    async updateTemplateAfterSignSettingAsync(): Promise<BaseResultModel> {
      const payload = {
        id: this.template.id,
        sourceDocumentType: this.template.sourceDocumentType,
        targetCabinet: this.targetCabinet,
        targetDocumentType: this.targetDocumentType,
        indexAction: this.template.indexAction,
        referenceMappings: this.referenceMappings
      } as ArenaSignTemplateAfterSignSettingUpdateModel;

      if (this.arenaSignApi === ArenaSignApi.DocuSign) {
        return await DocuSignConfigurationService.updateTemplateAfterSignSettingAsync(payload);
      }
      return await ScriveConfigurationService.updateTemplateAfterSignSettingAsync(payload);
    }
  },
  mounted() {
    this.setupData();
    this.isLoading = false;
  },
  watch: {
    targetDocumentType(this: any, newValue: DocumentTypeModel) {
      Object.keys(this.referenceMappings).forEach((x: string) => {
        if (!newValue?.references.find((r) => r.id === this.referenceMappings[x])) {
          this.referenceMappings[x] = -1;
        }
      });
    }
  },
  components: {
    LoadingSpinner,
    MStoreFormSelect
  }
});
